"use client";

import type React from "react";
import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "@/lib/motion";
import { Check, Headphones, ShieldCheck, Smartphone, Users } from "@/components/icons";

/* Ambient looping visuals for the four supporting Why Proxidize cards — airy,
   frameless compositions of a few large soft elements (floating circles,
   bubbles, one pill each), token-based so they read in both themes. Every loop
   shares the same 6s cycle and every one holds a visible resting state, so a
   card is never a blank box when its loop is off. All are decorative. */

const LOOP = { duration: 6, repeat: Infinity, ease: "easeInOut" as const };

/** Ease back to the resting state when a loop switches off (off-screen, reduced
 *  motion) instead of snapping. */
const SETTLE = { duration: 0.25, ease: "easeOut" as const };

const GREEN_TINT = "color-mix(in srgb, var(--brand-green) 14%, transparent)";
const CYAN_TINT = "color-mix(in srgb, var(--brand-yellow) 14%, transparent)";
const NEUTRAL_TINT = "color-mix(in srgb, var(--foreground) 6%, transparent)";
const HAIRLINE = "color-mix(in srgb, var(--foreground) 12%, transparent)";

/** Ambient loops only run while their card is on screen — four infinite
 *  timelines off-screen is four timelines' worth of frames spent on nothing.
 *  Returns false under reduced motion too, so each visual has a single flag to
 *  branch on and one resting state to fall back to. */
function useAmbientLoop() {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const inView = useInView(ref, { amount: 0.5 });
  return { ref, active: !reduced && inView };
}

interface LabelPillProps {
  children: React.ReactNode;
}

/** Floating label chip — the small uppercase pill from the reference cards. */
function LabelPill({ children }: LabelPillProps) {
  return (
    <span
      className="border-border bg-popover rounded-full border px-2.5 py-1 text-[8px] font-semibold tracking-wider uppercase shadow-sm"
      style={{ color: "var(--brand-green)" }}
    >
      {children}
    </span>
  );
}

/* ── 1 · Ethically Sourced IPs — devices opting in along a consent chain ─── */

const CONSENT_DEVICES = [
  { id: "d1", at: 0.14, bob: 0 },
  { id: "d2", at: 0.26, bob: 0.5 },
  { id: "d3", at: 0.38, bob: 1 },
];

/* Confirmations clear here, and everything rewinds during the invisible tail
   that follows, so the loop seam never shows the reset. */
const CONSENT_OUT = 0.86;

export function ConsentTogglesVisual() {
  const { ref, active } = useAmbientLoop();
  return (
    <div
      ref={ref}
      className="flex h-28 w-full flex-col items-center justify-center gap-3"
      aria-hidden="true"
    >
      <div className="relative flex items-center gap-4">
        {/* The consent chain: a hairline track with a green fill that reaches
            each device just as it confirms. Sits behind the devices, which
            carry their own surface. */}
        <div className="pointer-events-none absolute inset-x-5.5 top-1/2 h-px">
          <span className="absolute inset-0" style={{ backgroundColor: HAIRLINE }} />
          <motion.span
            initial={{ scaleX: 0, opacity: 0 }}
            animate={
              active
                ? { scaleX: [0, 0, 1, 1, 1, 0], opacity: [0, 1, 1, 1, 0, 0] }
                : { scaleX: 1, opacity: 1 }
            }
            transition={active ? { ...LOOP, times: [0, 0.08, 0.44, 0.8, CONSENT_OUT, 1] } : SETTLE}
            className="absolute inset-0 origin-left"
            style={{ backgroundColor: "var(--brand-green)" }}
          />
        </div>

        {CONSENT_DEVICES.map((device) => (
          <motion.div
            key={device.id}
            initial={{ y: 0 }}
            animate={active ? { y: [0, -3, 0] } : { y: 0 }}
            transition={
              active
                ? { duration: 3.2, repeat: Infinity, ease: "easeInOut", delay: device.bob }
                : SETTLE
            }
            className="border-border bg-popover relative z-10 flex h-11 w-11 items-center justify-center rounded-full border shadow-sm"
          >
            <Smartphone size={16} className="text-muted-foreground" aria-hidden="true" />

            {/* One ring pulse at the moment this device opts in. */}
            {active && (
              <motion.span
                initial={{ opacity: 0, scale: 1 }}
                animate={{ opacity: [0, 0, 0.5, 0, 0], scale: [1, 1, 1.45, 1.7, 1.7] }}
                transition={{
                  ...LOOP,
                  times: [0, device.at, device.at + 0.03, device.at + 0.2, 1],
                }}
                className="absolute inset-0 rounded-full"
                style={{ border: "1.5px solid var(--brand-green)" }}
              />
            )}

            <motion.span
              initial={{ opacity: 0, scale: 0.5 }}
              animate={
                active
                  ? { opacity: [0, 0, 1, 1, 0], scale: [0.5, 0.5, 1, 1, 0.6] }
                  : { opacity: 1, scale: 1 }
              }
              transition={
                active
                  ? { ...LOOP, times: [0, device.at, device.at + 0.06, CONSENT_OUT, 0.94] }
                  : SETTLE
              }
              className="border-popover absolute -right-0.5 -bottom-0.5 flex h-4 w-4 items-center justify-center rounded-full border-2"
              style={{ backgroundColor: "var(--brand-green)" }}
            >
              <Check size={8} style={{ color: "var(--background)" }} aria-hidden="true" />
            </motion.span>
          </motion.div>
        ))}
      </div>
      <LabelPill>Per-device opt-in</LabelPill>
    </div>
  );
}

/* ── 2 · KYC & Compliance — an ID scanned, then stamped verified ─────────── */

/* The sweep crosses the card's inner height as a transform rather than by
   animating `top`, so no frame of it costs a layout pass. */
const SCAN_FROM = 10;
const SCAN_TO = 74;

export function KycScanVisual() {
  const { ref, active } = useAmbientLoop();
  return (
    <div ref={ref} className="flex h-28 w-full items-center justify-center" aria-hidden="true">
      <div className="relative">
        <div className="border-border bg-popover relative w-40 overflow-hidden rounded-xl border p-3 shadow-sm">
          <div className="flex items-center gap-2.5">
            <span
              className="relative h-8 w-8 shrink-0 rounded-full"
              style={{ backgroundColor: CYAN_TINT }}
            >
              {/* The identity clears once the sweep has passed it. */}
              <motion.span
                initial={{ opacity: 0, scale: 0.6 }}
                animate={
                  active
                    ? { opacity: [0, 0, 1, 1, 0], scale: [0.6, 0.6, 1, 1, 0.7] }
                    : { opacity: 1, scale: 1 }
                }
                transition={active ? { ...LOOP, times: [0, 0.46, 0.54, 0.88, 0.94] } : SETTLE}
                className="border-popover absolute -right-0.5 -bottom-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full border-2"
                style={{ backgroundColor: "var(--brand-green)" }}
              >
                <Check size={7} style={{ color: "var(--background)" }} aria-hidden="true" />
              </motion.span>
            </span>
            <span className="flex min-w-0 flex-col gap-1.5">
              <span className="bg-foreground/15 h-1.5 w-16 rounded-full" />
              <span className="bg-foreground/10 h-1.5 w-10 rounded-full" />
            </span>
          </div>
          <div className="mt-3 flex flex-col gap-1.5">
            <span className="bg-foreground/10 h-1.5 w-full rounded-full" />
            <span className="bg-foreground/10 h-1.5 w-2/3 rounded-full" />
          </div>

          {active && (
            <motion.span
              initial={{ y: SCAN_FROM, opacity: 0 }}
              animate={{
                y: [SCAN_FROM, SCAN_FROM, SCAN_TO, SCAN_TO, SCAN_FROM],
                opacity: [0, 1, 1, 0, 0],
              }}
              transition={{ ...LOOP, times: [0, 0.12, 0.42, 0.48, 1] }}
              className="absolute inset-x-2 top-0 h-px"
              style={{
                background: "linear-gradient(90deg, transparent, var(--brand-green), transparent)",
              }}
            />
          )}

          {/* The card rims green once the sweep clears. */}
          <motion.span
            initial={{ opacity: 0 }}
            animate={active ? { opacity: [0, 0, 1, 1, 0] } : { opacity: 1 }}
            transition={active ? { ...LOOP, times: [0, 0.48, 0.56, 0.88, 0.94] } : SETTLE}
            className="pointer-events-none absolute inset-0 rounded-xl"
            style={{ border: "1px solid var(--brand-green)" }}
          />
        </div>

        <motion.span
          initial={{ opacity: 0, scale: 0.85 }}
          animate={
            active
              ? { opacity: [0, 0, 1, 1, 0], scale: [0.85, 0.85, 1, 1, 0.85] }
              : { opacity: 1, scale: 1 }
          }
          transition={active ? { ...LOOP, times: [0, 0.5, 0.56, 0.88, 0.94] } : SETTLE}
          className="absolute -bottom-2.5 left-1/2 flex items-center gap-1 rounded-full px-2.5 py-1 whitespace-nowrap shadow-sm"
          style={{
            x: "-50%",
            backgroundColor: "var(--brand-green)",
            color: "var(--background)",
          }}
        >
          <ShieldCheck size={10} aria-hidden="true" />
          <span className="text-[8px] font-semibold tracking-wider uppercase">KYC Verified</span>
        </motion.span>
      </div>
    </div>
  );
}

/* ── 3 · Dedicated Support — an agent on the line, typing then answering ── */

const TYPING_DOTS = ["t1", "t2", "t3"];

/* Both bubbles stay on screen for the whole cycle; only the agent's reply swaps
   between typing dots and the answer, so the card never reads as an empty box.
   Same times on the dots overlay and the text, inverted, so they cross cleanly. */
const REPLY_TIMES = [0, 0.36, 0.44, 0.88, 0.96];

export function SupportChatVisual() {
  const { ref, active } = useAmbientLoop();
  return (
    <div
      ref={ref}
      className="flex h-28 w-full flex-col items-center justify-center gap-2"
      aria-hidden="true"
    >
      <div className="flex w-44 flex-col gap-1.5">
        {/* Customer message — always present, breathing once per cycle. */}
        <motion.div
          initial={{ y: 0 }}
          animate={active ? { y: [0, -2, 0] } : { y: 0 }}
          transition={active ? LOOP : SETTLE}
          className="self-end rounded-2xl rounded-br-md px-3 py-1.5 text-[10px] shadow-sm"
          style={{ backgroundColor: "var(--brand-green)", color: "var(--background)" }}
        >
          Need help with rotation
        </motion.div>

        <div className="flex items-end gap-1.5 self-start">
          {/* Agent avatar — a ring pulsing outward plus a steady online dot. */}
          <span className="border-border bg-popover relative flex h-5 w-5 shrink-0 items-center justify-center rounded-full border shadow-sm">
            <Headphones size={10} className="text-muted-foreground" aria-hidden="true" />
            {active && (
              <motion.span
                initial={{ opacity: 0.45, scale: 1 }}
                animate={{ opacity: [0.45, 0, 0.45], scale: [1, 1.85, 1] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
                className="absolute inset-0 rounded-full"
                style={{ border: "1.5px solid var(--brand-green)" }}
              />
            )}
            <span
              className="border-popover absolute -right-0.5 -bottom-0.5 h-2 w-2 rounded-full border-2"
              style={{ backgroundColor: "var(--brand-green)" }}
            />
          </span>

          {/* Reply bubble — the answer sits in flow so the height never jumps;
              the typing dots ride over it on an opaque overlay. */}
          <div className="border-border bg-popover relative rounded-2xl rounded-bl-md border px-3 py-1.5 shadow-sm">
            <motion.span
              initial={{ opacity: 0 }}
              animate={active ? { opacity: [0, 0, 1, 1, 0] } : { opacity: 1 }}
              transition={active ? { ...LOOP, times: REPLY_TIMES } : SETTLE}
              className="text-foreground block text-[10px] leading-snug"
            >
              Rotating per request now
            </motion.span>
            {active && (
              <motion.span
                initial={{ opacity: 1 }}
                animate={{ opacity: [1, 1, 0, 0, 1] }}
                transition={{ ...LOOP, times: REPLY_TIMES }}
                className="bg-popover absolute inset-0 flex items-center justify-center gap-1 rounded-2xl rounded-bl-md"
              >
                {TYPING_DOTS.map((dot, i) => (
                  <motion.span
                    key={dot}
                    initial={{ opacity: 0.35 }}
                    animate={{ opacity: [0.35, 1, 0.35] }}
                    transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.18 }}
                    className="bg-muted-foreground h-1 w-1 rounded-full"
                  />
                ))}
              </motion.span>
            )}
          </div>
        </div>
      </div>
      <LabelPill>Live chat &amp; email</LabelPill>
    </div>
  );
}

/* ── 4 · Sub-user Management — one account, scoped sub-users ─────────────── */

const SUB_USERS = [
  {
    id: "su1",
    initials: "SC",
    label: "Scraping",
    start: 0.06,
    bg: GREEN_TINT,
    fg: "var(--brand-green)",
  },
  {
    id: "su2",
    initials: "GR",
    label: "Growth",
    start: 0.36,
    bg: CYAN_TINT,
    fg: "var(--yellow-accent)",
  },
  {
    id: "su3",
    initials: "QA",
    label: "QA",
    start: 0.66,
    bg: NEUTRAL_TINT,
    fg: "var(--muted-foreground)",
  },
];

/** The sub-user that stays highlighted when the loop is off, so the resting
 *  state still shows what the visual is about. */
const SETTLED_USER = "su2";

export function SubUsersVisual() {
  const { ref, active } = useAmbientLoop();
  return (
    <div
      ref={ref}
      className="flex h-28 w-full flex-col items-center justify-center"
      aria-hidden="true"
    >
      {/* The parent account every sub-user hangs off. */}
      <span className="border-border bg-popover flex items-center gap-1 rounded-full border px-2 py-1 shadow-sm">
        <Users size={9} className="text-muted-foreground" aria-hidden="true" />
        <span className="text-muted-foreground text-[8px] font-semibold tracking-wider uppercase">
          Account
        </span>
      </span>

      <div className="relative flex gap-5 pt-3.5 pb-6">
        {/* Spine joining the three drops back to the account. */}
        <span
          className="absolute top-0 right-5 left-5 h-px"
          style={{ backgroundColor: HAIRLINE }}
        />

        {SUB_USERS.map((user) => {
          const s = user.start;
          const times = [0, s, s + 0.05, s + 0.26, s + 0.31];
          const settled = user.id === SETTLED_USER;
          return (
            <div key={user.id} className="relative flex flex-col items-center">
              {/* Drop from the spine into this avatar, lighting green while the
                  sub-user is the active one. */}
              <span
                className="absolute -top-3.5 left-1/2 h-3.5 w-px -translate-x-1/2"
                style={{ backgroundColor: HAIRLINE }}
              />
              <motion.span
                initial={{ opacity: 0 }}
                animate={active ? { opacity: [0, 0, 1, 1, 0] } : { opacity: settled ? 1 : 0 }}
                transition={active ? { ...LOOP, times } : SETTLE}
                className="absolute -top-3.5 left-1/2 h-3.5 w-px"
                style={{ x: "-50%", backgroundColor: "var(--brand-green)" }}
              />

              <motion.span
                initial={{ scale: 1 }}
                animate={active ? { scale: [1, 1, 1.1, 1.1, 1] } : { scale: settled ? 1.08 : 1 }}
                transition={active ? { ...LOOP, times } : SETTLE}
                className="relative flex h-10 w-10 items-center justify-center rounded-full shadow-sm"
                style={{ backgroundColor: user.bg }}
              >
                <span className="text-[11px] font-semibold" style={{ color: user.fg }}>
                  {user.initials}
                </span>
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={active ? { opacity: [0, 0, 1, 1, 0] } : { opacity: settled ? 1 : 0 }}
                  transition={active ? { ...LOOP, times } : SETTLE}
                  className="absolute -inset-1 rounded-full"
                  style={{ border: "1.5px solid var(--brand-green)" }}
                />
              </motion.span>

              {/* Scope chip — absolute so three of them never fight for width. */}
              <motion.span
                initial={{ opacity: 0, y: 3 }}
                animate={
                  active
                    ? { opacity: [0, 0, 1, 1, 0], y: [3, 3, 0, 0, 3] }
                    : { opacity: settled ? 1 : 0, y: 0 }
                }
                transition={active ? { ...LOOP, times } : SETTLE}
                className="border-border bg-popover absolute -bottom-6 rounded-full border px-2 py-0.5 text-[8px] font-semibold tracking-wider whitespace-nowrap uppercase shadow-sm"
                style={{ color: "var(--brand-green)" }}
              >
                {user.label}
              </motion.span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
